import { NOTES_CATEGORY_FILTER_ALL, sanitizeNotesCategoryFilter } from "../constants/notesPreferences";

export const NOTES_SELECTION_LS_KEY = "vocabularium_notes_selection";
export const NOTES_SCROLL_LS_KEY = "vocabularium_notes_scroll";

function readMap(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(key, map) {
  try {
    localStorage.setItem(key, JSON.stringify(map && typeof map === "object" ? map : {}));
  } catch {
    // quota / mode privé : on ignore
  }
}

export function readNotesSelectionMap() {
  return readMap(NOTES_SELECTION_LS_KEY);
}

export function writeNotesSelectionMap(map) {
  writeMap(NOTES_SELECTION_LS_KEY, map);
}

export function rememberNotesSelection(categoryFilter, noteId) {
  const key = sanitizeNotesCategoryFilter(categoryFilter);
  const map = readNotesSelectionMap();
  if (noteId) {
    map[key] = String(noteId);
  } else {
    delete map[key];
  }
  writeNotesSelectionMap(map);
  return map;
}

export function readNotesSelection(categoryFilter) {
  const key = sanitizeNotesCategoryFilter(categoryFilter);
  const value = readNotesSelectionMap()[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function readNotesScrollMap() {
  return readMap(NOTES_SCROLL_LS_KEY);
}

export function writeNotesScrollMap(map) {
  writeMap(NOTES_SCROLL_LS_KEY, map);
}

export function getNotesScrollStorageKey(categoryFilter) {
  return sanitizeNotesCategoryFilter(categoryFilter || NOTES_CATEGORY_FILTER_ALL);
}

export const NOTES_CARET_LS_KEY = "vocabularium_notes_caret";

export function readNotesCaretMap() {
  return readMap(NOTES_CARET_LS_KEY);
}

export function writeNotesCaretMap(map) {
  writeMap(NOTES_CARET_LS_KEY, map);
}

export function rememberNotesCaret(noteId, snapshot) {
  const id = String(noteId || "").trim();
  if (!id || !snapshot || typeof snapshot !== "object") return;

  const field = ["title", "category", "content"].includes(snapshot.field)
    ? snapshot.field
    : "content";
  const start =
    Number.isFinite(Number(snapshot.start)) && Number(snapshot.start) >= 0
      ? Math.round(Number(snapshot.start))
      : null;
  const end =
    Number.isFinite(Number(snapshot.end)) && Number(snapshot.end) >= 0
      ? Math.round(Number(snapshot.end))
      : start;
  const scrollTop =
    Number.isFinite(Number(snapshot.scrollTop)) && Number(snapshot.scrollTop) >= 0
      ? Math.round(Number(snapshot.scrollTop))
      : 0;

  const map = readNotesCaretMap();
  map[id] = { field, start, end, scrollTop };
  writeNotesCaretMap(map);
}

export function readNotesCaret(noteId) {
  const id = String(noteId || "").trim();
  if (!id) return null;
  const raw = readNotesCaretMap()[id];
  if (!raw || typeof raw !== "object") return null;
  return {
    field: ["title", "category", "content"].includes(raw.field) ? raw.field : "content",
    start:
      Number.isFinite(Number(raw.start)) && Number(raw.start) >= 0
        ? Math.round(Number(raw.start))
        : null,
    end:
      Number.isFinite(Number(raw.end)) && Number(raw.end) >= 0
        ? Math.round(Number(raw.end))
        : null,
    scrollTop:
      Number.isFinite(Number(raw.scrollTop)) && Number(raw.scrollTop) >= 0
        ? Math.round(Number(raw.scrollTop))
        : 0,
  };
}

export function forgetNotesCaret(noteId) {
  const id = String(noteId || "").trim();
  if (!id) return;
  const map = readNotesCaretMap();
  if (!(id in map)) return;
  delete map[id];
  writeNotesCaretMap(map);
}

export const NOTES_VIEW_MODE_LS_KEY = "vocabularium_notes_view_mode";

export const NOTES_VIEW_MODES = {
  READ: "read",
  EDIT: "edit",
};

export function sanitizeNotesViewMode(raw) {
  const value = String(raw || "").trim();
  return value === NOTES_VIEW_MODES.EDIT
    ? NOTES_VIEW_MODES.EDIT
    : NOTES_VIEW_MODES.READ;
}

export function readNotesViewMode() {
  try {
    return sanitizeNotesViewMode(localStorage.getItem(NOTES_VIEW_MODE_LS_KEY));
  } catch {
    return NOTES_VIEW_MODES.READ;
  }
}

export function rememberNotesViewMode(mode) {
  const next = sanitizeNotesViewMode(mode);
  try {
    localStorage.setItem(NOTES_VIEW_MODE_LS_KEY, next);
  } catch {
    // quota / mode privé : on ignore
  }
  return next;
}

export const NOTES_SEARCH_LS_KEY = "vocabularium_notes_search";
export const NOTES_SEARCH_SCOPE_LS_KEY = "vocabularium_notes_search_scope";

export const NOTES_SEARCH_SCOPES = {
  TITLE: "title",
  CONTENT: "content",
};

export function sanitizeNotesSearchScope(raw) {
  const value = String(raw || "").trim();
  return value === NOTES_SEARCH_SCOPES.CONTENT
    ? NOTES_SEARCH_SCOPES.CONTENT
    : NOTES_SEARCH_SCOPES.TITLE;
}

export function readNotesSearchQuery() {
  try {
    return localStorage.getItem(NOTES_SEARCH_LS_KEY) || "";
  } catch {
    return "";
  }
}

export function writeNotesSearchQuery(query = "") {
  const value = String(query ?? "");
  try {
    if (value) {
      localStorage.setItem(NOTES_SEARCH_LS_KEY, value);
    } else {
      localStorage.removeItem(NOTES_SEARCH_LS_KEY);
    }
  } catch {
    // quota / mode privé : on ignore
  }
}

export function readNotesSearchScope() {
  try {
    return sanitizeNotesSearchScope(localStorage.getItem(NOTES_SEARCH_SCOPE_LS_KEY));
  } catch {
    return NOTES_SEARCH_SCOPES.TITLE;
  }
}

export function writeNotesSearchScope(scope = NOTES_SEARCH_SCOPES.TITLE) {
  const value = sanitizeNotesSearchScope(scope);
  try {
    localStorage.setItem(NOTES_SEARCH_SCOPE_LS_KEY, value);
  } catch {
    // quota / mode privé : on ignore
  }
  return value;
}
